import { supabase, supabaseUrl } from '@/lib/supabase';
import {
  type FoundContactEmailSource,
  type FoundContactEmailStatus,
  type OutreachEnrollmentStatus,
  type OutreachMessageStatus,
  type OutreachSettings,
  type OutreachStep,
  parseOutreachSettings,
  parseOutreachSteps,
} from '@shared/outreach';

/**
 * « Trouver et contacter » (03/11/2026): what the sequence page reads (under
 * RLS, live) and the gestures it sends to the `outreach` function.
 */

export interface FoundContact {
  id: string;
  appRecordId: string;
  fullName: string;
  jobTitle: string | null;
  origin: 'crm' | 'dossier' | 'web' | 'manual';
  sourceUrl: string | null;
  sourceQuote: string | null;
  reason: string | null;
  rank: number;
  state: 'proposed' | 'chosen' | 'dismissed';
  email: string | null;
  emailStatus: FoundContactEmailStatus | null;
  emailSource: FoundContactEmailSource | null;
  emailMissing: 'not_found' | 'no_provider' | 'opposed' | null;
  phone: string | null;
  phoneMissing: 'not_found' | 'no_provider' | null;
}

export interface OutreachMessage {
  id: string;
  stepIndex: number;
  subject: string;
  body: string;
  status: OutreachMessageStatus;
  scheduledFor: string | null;
  sentAt: string | null;
  error: string | null;
}

export interface OutreachEnrollment {
  id: string;
  appRecordId: string;
  foundContactId: string;
  ownerUserId: string;
  toEmail: string;
  status: OutreachEnrollmentStatus;
  stopReason: string | null;
  messages: OutreachMessage[];
}

export interface OutreachJobState {
  kind: 'find_people' | 'find_email' | 'find_phone' | 'write';
  subjectId: string;
  status: 'queued' | 'running' | 'done' | 'failed';
  error: string | null;
}

export interface OutreachState {
  sequence: { id: string; steps: OutreachStep[]; settings: OutreachSettings } | null;
  contacts: FoundContact[];
  enrollments: OutreachEnrollment[];
  jobs: OutreachJobState[];
}

type Row = Record<string, unknown>;
const str = (value: unknown): string | null => typeof value === 'string' ? value : null;

const toContact = (row: Row): FoundContact => ({
  id: String(row.id), appRecordId: String(row.app_record_id), fullName: String(row.full_name), jobTitle: str(row.job_title),
  origin: row.origin as FoundContact['origin'], sourceUrl: str(row.source_url), sourceQuote: str(row.source_quote), reason: str(row.reason),
  rank: Number(row.rank ?? 1), state: row.state as FoundContact['state'], email: str(row.email),
  emailStatus: str(row.email_status) as FoundContactEmailStatus | null, emailSource: str(row.email_source) as FoundContactEmailSource | null,
  emailMissing: str(row.email_missing) as FoundContact['emailMissing'], phone: str(row.phone), phoneMissing: str(row.phone_missing) as FoundContact['phoneMissing'],
});

const toMessage = (row: Row): OutreachMessage => ({
  id: String(row.id), stepIndex: Number(row.step_index), subject: String(row.subject ?? ''), body: String(row.body ?? ''),
  status: row.status as OutreachMessageStatus, scheduledFor: str(row.scheduled_for), sentAt: str(row.sent_at), error: str(row.error),
});

/** Everything of one app's sequence page; the sequence is the reader's own (one per sender). */
export const loadOutreachState = async (toolAppId: string, userId: string): Promise<OutreachState> => {
  const [sequence, contacts, enrollments, jobs] = await Promise.all([
    supabase.from('outreach_sequences').select('id, steps, settings').eq('tool_app_id', toolAppId).eq('owner_user_id', userId).maybeSingle(),
    supabase.from('found_contacts').select('*').eq('tool_app_id', toolAppId).order('rank'),
    supabase.from('outreach_enrollments')
      .select('id, app_record_id, found_contact_id, owner_user_id, to_email, status, stop_reason, outreach_messages(id, step_index, subject, body, status, scheduled_for, sent_at, error)')
      .eq('tool_app_id', toolAppId).order('created_at', { ascending: false }),
    supabase.from('outreach_jobs').select('kind, subject_id, status, error').eq('tool_app_id', toolAppId).in('status', ['queued', 'running', 'failed']),
  ]);
  for (const result of [sequence, contacts, enrollments, jobs]) {
    if (result.error) throw new Error('La séquence n’a pas pu être chargée.');
  }
  return {
    sequence: sequence.data
      ? { id: String(sequence.data.id), steps: parseOutreachSteps(sequence.data.steps), settings: parseOutreachSettings(sequence.data.settings) }
      : null,
    contacts: ((contacts.data ?? []) as Row[]).map(toContact),
    enrollments: ((enrollments.data ?? []) as Row[]).map((row) => ({
      id: String(row.id), appRecordId: String(row.app_record_id), foundContactId: String(row.found_contact_id),
      ownerUserId: String(row.owner_user_id), toEmail: String(row.to_email), status: row.status as OutreachEnrollmentStatus,
      stopReason: str(row.stop_reason),
      messages: (Array.isArray(row.outreach_messages) ? row.outreach_messages as Row[] : []).map(toMessage).sort((a, b) => a.stepIndex - b.stepIndex),
    })),
    jobs: ((jobs.data ?? []) as Row[]).map((row) => ({
      kind: row.kind as OutreachJobState['kind'], subjectId: String(row.subject_id), status: row.status as OutreachJobState['status'], error: str(row.error),
    })),
  };
};

/** Live: a person found, an address, an e-mail written or sent changes the page. */
export const watchOutreach = (toolAppId: string, onChange: () => void): (() => void) => {
  const channel = supabase.channel(`outreach-${toolAppId}-${Math.random().toString(36).slice(2)}`);
  for (const table of ['found_contacts', 'outreach_enrollments', 'outreach_jobs']) {
    channel.on('postgres_changes', { event: '*', schema: 'public', table, filter: `tool_app_id=eq.${toolAppId}` }, onChange);
  }
  channel.on('postgres_changes', { event: '*', schema: 'public', table: 'outreach_messages' }, onChange);
  channel.subscribe();
  return () => { void supabase.removeChannel(channel); };
};

/** A gesture of the page; a refusal comes back with its sentence. */
export const outreachAction = async <T = Record<string, unknown>>(body: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.functions.invoke('outreach', { body });
  if (error) {
    const context = (error as { context?: unknown }).context;
    const payload = context instanceof Response ? await context.json().catch(() => null) as { error?: string } | null : null;
    throw new Error(payload?.error ?? 'L’action n’a pas pu être faite.');
  }
  return data as T;
};

/** The public refusal page: no session, the token is the only key. */
export const optOut = async (token: string, action: 'read' | 'confirm') => {
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
  const response = await fetch(`${supabaseUrl}/functions/v1/outreach-opt-out`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: anonKey, Authorization: `Bearer ${anonKey}` },
    body: JSON.stringify({ token, action }),
  });
  const payload = await response.json().catch(() => null) as { sender?: string | null; company?: string | null; email?: string; done?: boolean; error?: string } | null;
  if (!response.ok || !payload) throw new Error(payload?.error ?? 'Le lien n’a pas pu être lu.');
  return payload;
};
